import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useTheme } from '../context/ThemeContext'
import { useProviders } from '../hooks/useProviders.ts'
import { useSelectedModel } from '../context/SelectedModelContext'

import ModelIcon from './ModelIcon.tsx'
import ModelCard from './model-card/ModelCard.tsx'
import ModelCardsGridSkeleton from './model-card/ModelCardsGridSkeleton.tsx'

import type { Model } from '../data/models.ts'
import { BOTS_MAIN_FALLBACK_CATEGORIES, POPULAR_FILTER_SLUG } from '../pages/bots/config/botsCategories.ts'
import { isTtsModelId, isTtsRoute, TTS_ROUTE } from '../pages/generate/utils/ttsModels'

interface Props {
	selected: Model | null
	onSelect: (model: Model) => void
	placeholder?: string
}

interface PageCategoryItem {
	id: string
	name: string
}

export default function ModelSelector({ selected, onSelect, placeholder }: Props) {
	const { isGenerating, activeAgent } = useSelectedModel()
	const [open, setOpen] = useState(false)
	const [closing, setClosing] = useState(false)
	const [search, setSearch] = useState('')
	// Выбор юзера; null = дефолт (первый чип: «Популярные» при наполненной
	// подборке, иначе первая категория). «Все» удалено (владелец, 2026-08-13).
	const [categoryChoice, setCategoryChoice] = useState<string | null>(null)

	const ref = useRef<HTMLDivElement>(null)
	const catRef = useRef<HTMLDivElement>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const isMobileRef = useRef(false)
	const closingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const openRef = useRef(false)

	const closeDropdown = useCallback(() => {
		if (!openRef.current) return
		setClosing(true)
		closingTimerRef.current = setTimeout(() => {
			setOpen(false)
			setClosing(false)
		}, 280)
	}, [])

	useEffect(() => {
		openRef.current = open
	}, [open])

	const toggleDropdown = useCallback(() => {
		if (activeAgent) return
		if (open) {
			closeDropdown()
		} else {
			if (closingTimerRef.current) {
				clearTimeout(closingTimerRef.current)
				setClosing(false)
			}
			setOpen(true)
		}
		// `activeAgent` обязательно держать в deps: без него useCallback
		// возвращает кэшированную функцию со старым closure'ом, и при
		// переходе с /chat/{agentId} на не-/chat маршрут (главная, /bots,
		// /files и т.п.) ModelSelector ремаунтится с key='all' раньше чем
		// Chat'овский cleanup успевает обнулить activeAgent — функция
		// захватывает {agent} и блокирует клик после того как контекст
		// уже стал null. Подробнее см. коммит "Селект моделей: разблокировка".
	}, [open, closeDropdown, activeAgent])

	useEffect(() => {
		return () => {
			if (closingTimerRef.current) clearTimeout(closingTimerRef.current)
		}
	}, [])

	const { widgetModels, widgetTools, loading, widgetFilters, apiAssignments } = useProviders()
	const navigate = useNavigate()
	const location = useLocation()
	const { theme } = useTheme()

	// Карточки-инструменты — отдельный список из хука, а не подмешанные в
	// widgetModels: см. комментарий в useProviders (иначе попадут в пикеры
	// генерации). Здесь склеиваем в ОДИН порядок по showcaseOrder — это общая
	// шкала sort_order моделей и инструментов, которую задаёт drag-сортировка
	// во вкладке «Модели» админки. Инструмент стоит ровно там, куда его положили.
	const widgetItems = useMemo<Model[]>(
		() => [...widgetModels, ...widgetTools].sort(
			(a, b) => (a.showcaseOrder ?? 9999) - (b.showcaseOrder ?? 9999),
		),
		[widgetModels, widgetTools],
	)

	const isDraggingRef = useRef(false)
	const startXRef = useRef(0)
	const scrollLeftRef = useRef(0)

	// «Популярные» — кураторский фильтр level 1 (slug=popular), состав задан
	// назначениями в админке. Пустая подборка чип не показывает.
	const popularFilter = useMemo(
		() => widgetFilters.find((f) => f.level === 1 && f.slug === POPULAR_FILTER_SLUG) || null,
		[widgetFilters],
	)
	const popularKeys = useMemo(() => {
		if (!popularFilter) return null
		const keys = new Set(
			apiAssignments
				.filter((a) => a.filter_id === popularFilter.id)
				.map((a) => `${a.provider}:${a.model_id}`),
		)
		return keys.size > 0 ? keys : null
	}, [popularFilter, apiAssignments])

	const categories = useMemo<PageCategoryItem[]>(() => {
		const popular = popularFilter && popularKeys
			? [{ id: POPULAR_FILTER_SLUG, name: popularFilter.name || 'Популярные' }]
			: []
		// Основа: либо фильтры из админки, либо фоллбэк
		const base: PageCategoryItem[] = widgetFilters.length > 0
			? [
				...popular,
				...widgetFilters
					.filter((filter) => filter.level === 1)
					.filter((filter) => filter.slug !== POPULAR_FILTER_SLUG)
					.map((filter) => ({
						id: filter.slug,
						name: filter.name,
					})),
			]
			: [...popular, ...BOTS_MAIN_FALLBACK_CATEGORIES]

		// Автоматическое дополнение: если в widgetItems есть категории,
		// которых нет в base — добавляем их из FALLBACK с человекочитаемыми названиями.
		// Сканируем и карточки-инструменты: иначе инструмент с категорией, под
		// которую нет ни одной модели, был бы виден только на чипе «Все».
		const existingIds = new Set(base.map((c) => c.id))
		const missingCategoryIds = new Set<string>()
		for (const model of widgetItems) {
			if (model.category && !existingIds.has(model.category)) {
				missingCategoryIds.add(model.category)
			}
		}

		if (missingCategoryIds.size > 0) {
			const fallbackMap = new Map(BOTS_MAIN_FALLBACK_CATEGORIES.map((c) => [c.id, c]))
			for (const id of missingCategoryIds) {
				const fallback = fallbackMap.get(id)
				if (fallback) base.push(fallback)
				else base.push({ id, name: id.charAt(0).toUpperCase() + id.slice(1) })
			}
		}

		return base
	}, [widgetFilters, widgetItems, popularFilter, popularKeys])

	// Дефолт — первый чип: «Популярные» при наполненной подборке, иначе
	// первая категория. Производная, не setState по загрузке (данные async).
	const category = categoryChoice ?? categories[0]?.id ?? 'text'

	useEffect(() => {
		if (typeof window === 'undefined') return

		const isDesktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches
		isMobileRef.current = !isDesktopPointer
	}, [])

	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				closeDropdown()
			}
		}

		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [closeDropdown])

	useEffect(() => {
		if (!open) {
			document.body.style.overflow = ''
			return
		}

		if (!isMobileRef.current) return

		const previous = document.body.style.overflow
		document.body.style.overflow = 'hidden'

		return () => {
			document.body.style.overflow = previous
		}
	}, [open])

	useEffect(() => {
		if (!open) return
		if (typeof window === 'undefined') return

		const isDesktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches
		if (!isDesktopPointer) return

		requestAnimationFrame(() => {
			searchInputRef.current?.focus()
		})
	}, [open])

	useEffect(() => {
		if (!open) return
		requestAnimationFrame(() => {
			const activeItem = listRef.current?.querySelector<HTMLElement>('.model-item.active')
			activeItem?.scrollIntoView({ block: 'center', behavior: 'instant' })
		})
	}, [open])

	const handleCatMouseDown = (e: React.MouseEvent) => {
		isDraggingRef.current = true
		startXRef.current = e.pageX - (catRef.current?.offsetLeft || 0)
		scrollLeftRef.current = catRef.current?.scrollLeft || 0
	}

	const handleCatMouseMove = (e: React.MouseEvent) => {
		if (!isDraggingRef.current) return
		if (isMobileRef.current) return
		e.preventDefault()

		const x = e.pageX - (catRef.current?.offsetLeft || 0)
		if (catRef.current) {
			catRef.current.scrollLeft = scrollLeftRef.current - (x - startXRef.current)
		}
	}

	const handleCatMouseUp = () => {
		isDraggingRef.current = false
	}

	const normalizedSearch = search.trim().toLowerCase()

	const filteredModels = useMemo(() => {
		return widgetItems.filter((model) => {
			if (category === POPULAR_FILTER_SLUG) {
				if (!(popularKeys?.has(`${model.provider}:${model.id}`) ?? false)) return false
			} else if (model.category !== category) {
				return false
			}

			if (!normalizedSearch) return true

			return (
				model.name.toLowerCase().includes(normalizedSearch) ||
				model.description.toLowerCase().includes(normalizedSearch) ||
				model.provider.toLowerCase().includes(normalizedSearch) ||
				model.id.toLowerCase().includes(normalizedSearch)
			)
		})
	}, [widgetItems, category, popularKeys, normalizedSearch])

	const handleModelSelect = (model: Model) => {
		// Карточка-инструмент — навигационная плитка: уводим на её страницу и НЕ
		// зовём onSelect, иначе в шапку и в контекст генерации уедет объект,
		// который не является настоящей моделью адаптера.
		if (model.isTool && model.toolUrl) {
			closeDropdown()
			navigate(model.toolUrl)
			return
		}
		onSelect(model)
		closeDropdown()
		// Аудио живёт на своей странице (лента треков, кабинет) — выбор
		// аудио-модели уводит туда, а не оставляет на /generate (решение
		// владельца). Модель уже выбрана onSelect'ом — страница откроется с ней.
		// Модель озвучки (ElevenLabs) — на вкладку «Озвучка»: музыку она не пишет.
		if (model.category === 'audio' && isTtsModelId(model.id)) {
			if (!isTtsRoute(location.pathname)) navigate(TTS_ROUTE)
		} else if (model.category === 'audio' && !location.pathname.startsWith('/bots/audio')) {
			navigate('/bots/audio')
		}
		// Презентации — тоже свой раздел (инструмент слайдов), а не старый
		// /generate (решение владельца 2026-08-13).
		if (model.category === 'presentation' && !location.pathname.startsWith('/bots/tools/slides')) {
			navigate('/bots/tools/slides')
		}
	}

	return (
		<div className={`model-selector ${open ? 'is-open' : ''}`} ref={ref}>
			<button
				className={`model-selector-btn ${!selected && placeholder ? 'model-selector-btn--plain' : ''}`}
				onClick={toggleDropdown}
				type="button"
				disabled={isGenerating || !!activeAgent}
			>
				{activeAgent ? (
					<>
						<span className="model-icon">
							{activeAgent.iconUrl ? (
								<img src={activeAgent.iconUrl} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }} />
							) : (
								<span style={{ fontSize: 16 }}>🤖</span>
							)}
						</span>
						<span className="model-name">{activeAgent.title}</span>
					</>
				) : selected ? (
					<>
						<span className="model-icon">
							<ModelIcon icon={selected.icon} name={selected.name} size={20} />
						</span>
						<span className="model-name">{selected.name}</span>
					</>
				) : (
					<>
						<span className="model-icon model-icon-placeholder">
							{placeholder ? (
								<img
									src={`/icons/dots_${theme}.svg`}
									alt={`dots_${theme}`}
									width="10"
									height="10"
								/>
							) : (
								'?'
							)}
						</span>
						<span className="model-name">{placeholder || 'Выбрать нейросеть'}</span>
					</>
				)}

				<svg
					className={`chevron ${open ? 'open' : ''}`}
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<path d="M6 9l6 6 6-6" />
				</svg>
			</button>

			{(open || closing) && (
				<>
					<div className="model-selector-backdrop" onClick={closeDropdown} />

					<div className={`model-dropdown${closing ? " model-dropdown--closing" : ""}`}>
						<div className="model-search">
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<circle cx="11" cy="11" r="8" />
								<path d="M21 21l-4.35-4.35" />
							</svg>

							<input
								ref={searchInputRef}
								type="text"
								placeholder="Поиск нейросети"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</div>

						<div
							className="model-categories"
							ref={catRef}
							onMouseDown={handleCatMouseDown}
							onMouseMove={handleCatMouseMove}
							onMouseUp={handleCatMouseUp}
							onMouseLeave={handleCatMouseUp}
						>
							{categories.map((cat) => (
								<button
									key={cat.id}
									className={`category-btn ${category === cat.id ? 'active' : ''}`}
									onClick={() => setCategoryChoice(cat.id)}
									type="button"
								>
									{cat.name}
								</button>
							))}
						</div>

						<div className="model-list" ref={listRef}>
							{/* Группировка по категориям жила только под чипом «Все» —
							    вместе с ним ушла (владелец, 2026-08-13): каждый чип,
							    включая «Популярные», показывает плоский список. */}
							{loading ? (
								<ModelCardsGridSkeleton variant="selector" count={4} showRadio />
							) : filteredModels.length === 0 ? (
								<div className="model-selector-empty">Ничего не найдено</div>
							) : (
								filteredModels.map((model) => (
									<ModelCard
										key={`${model.provider}-${model.id}`}
										model={model}
										onClick={handleModelSelect}
										variant="selector"
										isActive={!model.isTool && selected?.id === model.id}
										showRadio
									/>
								))
							)}
						</div>

						<button
							className="model-all-link"
							onClick={() => {
								closeDropdown()
								navigate('/bots')
							}}
							type="button"
						>
							Все нейросети и возможности
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<path d="M5 12h14M12 5l7 7-7 7" />
							</svg>
						</button>
					</div>
				</>
			)}
		</div>
	)
}
